import { NumberField } from "@kobalte/core/number-field";
import { A } from "@solidjs/router";
import { stAnalytics } from "@storytell/analytics";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbCaretDown, TbCaretUp, TbCircleCheckFilled, TbCoin, TbUserPlus } from "solid-icons/tb";
import { createSignal, For, Show } from "solid-js";
import { Logo } from "~/components/brand";
import { GradientBorder } from "~/components/prompt/internal/components";

export const PricingPage = (props: { inModal?: boolean }) => {
  // TODO: @andi Hook values from BE;
  const dailyCredits = () => 1739;
  const dailyRefill = () => 30;
  const freeCredits = () => 1000;

  const [freeAmount, setFreeAmount] = createSignal(10);
  const [proAmount, setProAmount] = createSignal(100);

  const [period, setPeriod] = createSignal<"monthly" | "annually">("monthly");

  return (
    <div>
      <Show when={!props.inModal}>
        <A href="/" class="block ml-10 mt-6">
          <Logo size={150} />
        </A>
      </Show>
      <div class="flex flex-col items-center px-3 py-10 gap-20">
        {/* <h2 class="text-3xl"> */}
        {/*   You have <span class="text-primary font-bold dark:text-blue-400">{dailyCredits().toLocaleString()}</span>{" "} */}
        {/*   credits remaining. */}
        {/* </h2> */}
        <div class="text-center">
          <h2 class="text-3xl font-semibold mb-3">Enjoy Free Unlimited Use of Storytell's New Beta</h2>
          <p class="text-secondary-text text-xl">Credit-based pricing coming soon</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 xl:gap-12">
          {/* EXPLORE COLUMN */}
          <div class="flex flex-col max-w-md items-center px-4 pt-8 pb-4 rounded-3xl border border-primary-border bg-tile">
            <h3 class="text-center text-2xl font-medium mb-3">Explore</h3>
            <p class="text-sm">Includes {freeCredits().toLocaleString()} free credits.</p>
            <p class="text-sm">Free credits refill at {dailyRefill().toLocaleString()}/day.</p>

            <div class="flex-auto flex flex-col items-center justify-center mt-10 mb-10">
              <p class="mb-3">Buy additional credits as needed</p>

              <NumberField
                class="relative mb-3"
                rawValue={freeAmount()}
                onRawValueChange={setFreeAmount}
                minValue={10}
                formatOptions={{
                  style: "currency",
                  maximumFractionDigits: 0,
                  currency: "USD",
                  roundingIncrement: 1,
                }}
              >
                <NumberField.Input class="w-40 text-xl outline-none focus:ring-2 ring-brand transition-shadow px-1 py-2 pr-4 rounded-lg border border-muted-border" />

                <div class="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-between py-1 pr-1">
                  <NumberField.IncrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                    <TbCaretUp />
                  </NumberField.IncrementTrigger>
                  <NumberField.DecrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                    <TbCaretDown />
                  </NumberField.DecrementTrigger>
                </div>
              </NumberField>

              <p class="mb-3">for {(freeAmount() * 1000).toLocaleString()} credits</p>
              <p class="text-sm text-secondary-text">Purchased credits expire after 12 months</p>
            </div>

            <StTooltip placement="top" content={<p>Coming Soon!</p>}>
              <StTooltipTrigger
                onClick={() => {
                  stAnalytics.track("click_tracking", { cta: "pricing-refill-explore", screen: undefined });
                }}
                class="rounded-lg cursor-pointer w-32 flex items-center justify-center gap-2 bg-brand text-on-brand px-4 py-2 self-end"
              >
                <TbCoin />
                Refill
              </StTooltipTrigger>
            </StTooltip>
          </div>

          {/* GO PRO COLUMN */}
          <div class="relative max-w-md">
            <GradientBorder scaleX={2} scaleY={2} />
            <div class="flex h-full flex-col items-center px-4 pt-8 pb-4 rounded-3xl border border-primary-border bg-tile relative">
              <h3 class="text-center text-2xl font-medium mb-3">Go Pro</h3>

              {/* Toggle for monthly/annual pricing */}
              <div class="relative flex items-center rounded-xl border border-muted-border overflow-hidden w-full max-w-xs">
                <div
                  class="absolute rounded-lg top-0 bottom-0 w-1/2 bg-brand transition-all duration-200"
                  style={{
                    left: period() === "monthly" ? "0" : "50%",
                  }}
                />

                <button
                  type="button"
                  data-active={period() === "monthly"}
                  class="w-1/2 h-10 rounded-lg bg-transparent focus:outline-none relative z-10 cursor-pointer data-[active=true]:text-on-brand"
                  onClick={() => setPeriod("monthly")}
                >
                  $20/month
                </button>
                <button
                  type="button"
                  data-active={period() === "annually"}
                  class="w-1/2 h-10 rounded-lg bg-transparent focus:outline-none relative z-10 cursor-pointer data-[active=true]:text-on-brand"
                  onClick={() => setPeriod("annually")}
                >
                  $200/year
                </button>
              </div>

              <div class="flex flex-col gap-2 mt-6 text-sm">
                <p>
                  Includes {period() === "monthly" ? "20,000" : "200,000"} credits /{" "}
                  {period() === "monthly" ? "month" : "year"}.
                </p>
              </div>

              <div class="flex-auto flex flex-col items-center mt-10 mb-10">
                <p>Buy additional credits as needed.</p>
                <p class="mb-4 text-sm">25% credit bonus</p>

                <NumberField
                  class="relative mb-3"
                  rawValue={proAmount()}
                  onRawValueChange={setProAmount}
                  minValue={5}
                  formatOptions={{
                    style: "currency",
                    maximumFractionDigits: 0,
                    currency: "USD",
                    roundingIncrement: 1,
                  }}
                >
                  <NumberField.Input class="w-40 text-xl outline-none focus:ring-2 ring-brand transition-shadow px-1 py-2 pr-4 rounded-lg border border-muted-border" />

                  <div class="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-between py-1 pr-1">
                    <NumberField.IncrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                      <TbCaretUp />
                    </NumberField.IncrementTrigger>
                    <NumberField.DecrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                      <TbCaretDown />
                    </NumberField.DecrementTrigger>
                  </div>
                </NumberField>

                <p class="mb-3">for {(proAmount() * 1000 * 1.25).toLocaleString()} credits</p>
              </div>

              <p class="mb-3 text-sm text-secondary-text max-w-sm">
                Purchased credits don't expire for as long as you remain a subscriber.
              </p>

              <StTooltip placement="top" content={<p>Coming Soon!</p>}>
                <StTooltipTrigger
                  onClick={() => {
                    stAnalytics.track("click_tracking", { cta: "pricing-subscribe-go-pro", screen: undefined });
                  }}
                  class="rounded-lg cursor-pointer w-32 flex items-center justify-center gap-2 bg-brand text-on-brand px-4 py-2 self-end"
                >
                  <TbUserPlus />
                  Subscribe
                </StTooltipTrigger>
              </StTooltip>
            </div>
          </div>

          {/* ENTERPRISE COLUMN */}

          <div class="flex flex-col max-w-md items-center px-4 pt-8 pb-4 rounded-3xl border border-primary-border bg-tile relative">
            <h3 class="text-center text-2xl font-medium mb-3">Enterprise Unlimited</h3>
            <p class="text-center mb-8 text-secondary-text max-w-sm">Custom Pricing</p>

            <ul class="flex flex-col gap-3 mb-8 px-4 flex-auto">
              <For
                each={[
                  "Embed Storytell in your own applications",
                  "Per-team budgets and access controls",
                  "Custom retention policies",
                  "Report on cross-team usage",
                  "Bring your own LLM API Keys",
                  "Bring your own on-prem LLMs",
                  "Credit Card or Invoice payment",
                  "Enterprise support SLAs",
                ]}
              >
                {(item) => (
                  <li class="flex items-start gap-2">
                    <TbCircleCheckFilled class="text-brand mt-1" />
                    {item}
                  </li>
                )}
              </For>
            </ul>

            <A
              target="_blank"
              href="https://web.storytell.ai/book-a-demo"
              onClick={() => {
                stAnalytics.track("click_tracking", { cta: "pricing-contact-sales-enterprise", screen: undefined });
              }}
              class="mb-2 block text-center w-full py-3 px-4 rounded-lg font-semibold transition-colors bg-panel hover:bg-panel-hover text-primary-text border border-primary-border"
            >
              Contact Sales
            </A>

            <span>
              Or email{" "}
              <a
                class="underline"
                onClick={() => {
                  stAnalytics.track("click_tracking", { cta: "pricing-email-sales-enterprise", screen: undefined });
                }}
                href="mailto:sales@storytell.ai?subject=Enterprise%20Sales%20Request&body=Please%20share%20more%20about%20your%20interest%20in%20Storytell…"
              >
                sales@storytell.ai
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
